import React, { useMemo, useState } from "react";
import { Row, Col, Form, Button, Card, Alert, Badge, Accordion } from "react-bootstrap";
import { FaPlus, FaExclamationTriangle } from "react-icons/fa";

import {
  Counter,
  SingleSelect,
  MultiSelect,
  StepHeader,
  Section,
  SectionDivider,
  StickyActionBar,
  HelpAndFloorsStep,
  dictByAreaItem,
  dictByKey,
} from "./Wizardshared";
import "./Specialitemswizard.css";

import {
  MOVE_SUB_TYPES,
  MOVE_AREAS,
  MOVE_HEAVY_ITEMS,
  ASSEMBLY_ELIGIBLE_KEYS,
  KITCHEN_ROOM_KEY,
  ASSEMBLY_SERVICE_OPTIONS,
  ASSEMBLY_LEVEL_OPTIONS,
  KITCHEN_SERVICE_OPTIONS,
} from "../config/Movewizardconfig";
import { useTranslation } from "react-i18next";

const CustomAreaItems = ({ areaKey, items, setItemField }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const existing = Object.values(items).filter((i) => i.area === areaKey);

  const add = () => {
    if (!name.trim()) return;
    setItemField(areaKey, name.trim(), "quantity", 1);
    setName("");
  };

  return (
    <div>
      {existing.map((it) => (
        <div key={it.item} className="mb-2 pb-2 border-bottom">
          <Row className="align-items-center">
            <Col xs={7} sm={5}>
              {it.item}
            </Col>
            <Col xs={5} sm={3}>
              <Counter value={it.quantity} onChange={(v) => setItemField(areaKey, it.item, "quantity", v)} />
            </Col>
          </Row>
          <div className="small text-muted mt-1 mb-1">{t("specialItems.furniture.size")}</div>
          <SingleSelect
            name={`${it.item}-custom-size`}
            options={[t("specialItems.furniture.small"), t("specialItems.furniture.medium"), t("specialItems.furniture.large"), t("specialItems.furniture.unknown")]}
            value={it.size}
            onChange={(v) => setItemField(areaKey, it.item, "size", v)}
          />
        </div>
      ))}
      <Row className="align-items-center">
        <Col xs={8}>
          <Form.Control placeholder={t("specialItems.furniture.enter_item_name")} value={name} onChange={(e) => setName(e.target.value)} />
        </Col>
        <Col xs={4}>
          <Button variant="outline-primary" onClick={add}>
            <FaPlus className="me-1" /> {t("specialItems.furniture.add")}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

const MoveSummary = ({ areas, items, heavyItems, assemblyAnswer, assemblyItems }) => {
  const { t } = useTranslation();
  const byArea = {};
  items.forEach((i) => {
    byArea[i.area] = byArea[i.area] || [];
    byArea[i.area].push(i);
  });

  return (
    <div>
      <StepHeader title={t("specialItems.furniture.summary")} />
      <div className="mb-3">
        {areas.map((a) => (
          <Badge bg="light" text="dark" className="border me-2 mb-1" key={a}>
            {a}
          </Badge>
        ))}
      </div>

      {Object.entries(byArea).map(([area, list]) => (
        <div key={area} className="mb-2">
          <strong>{area}:</strong>
          <ul className="mb-1">
            {list.map((i, idx) => (
              <li key={idx}>
                {i.quantity} × {i.item}
                {i.type ? `, ${i.type}` : ""}
                {i.size ? `, ${i.size}` : ""}
                {i.description ? ` — ${i.description}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {heavyItems.length > 0 && (
        <>
          <h6>{t("specialItems.summary.heavy_items")}</h6>
          <ul>
            {heavyItems.map((i, idx) => (
              <li key={idx}>
                {i.quantity} × {i.item}
                {i.size ? `, ${i.size}` : ""}
                {i.type ? `, ${i.type}` : ""}
              </li>
            ))}
          </ul>
        </>
      )}

      {assemblyAnswer === "Ja" && assemblyItems.length > 0 && (
        <>
          <h6>{t("specialItems.summary.assembly_service")}</h6>
          <ul>
            {assemblyItems.map((i, idx) => (
              <li key={idx}>
                {i.quantity ? `${i.quantity} × ` : ""}
                {i.item} — {i.service}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const MoveWizard = ({ onComplete, onEditRoute, initialData }) => {
  const { t } = useTranslation();
  const [moveSubType, setMoveSubType] = useState(() => initialData?.move_sub_type || "");
  const [areas, setAreas] = useState(() => initialData?.areas || []);
  const [items, setItems] = useState(() => dictByAreaItem(initialData?.items));
  const [heavyAnswer, setHeavyAnswer] = useState(
    () => initialData?.heavy_items_answer || (initialData?.heavy_items?.length ? "Ja" : "")
  );
  const [heavyItems, setHeavyItems] = useState(() => dictByKey(initialData?.heavy_items));
  const [assemblyAnswer, setAssemblyAnswer] = useState(() => initialData?.assembly_service?.answer || "");
  const [assemblyNotes, setAssemblyNotes] = useState(() => initialData?.assembly_service?.notes || "");
  const [assemblyConsult, setAssemblyConsult] = useState(() => !!initialData?.assembly_service?.consultation_requested);
  const [assemblyItems, setAssemblyItems] = useState(() => dictByKey(initialData?.assembly_service?.items));
  const [kitchenService, setKitchenService] = useState(() => initialData?.assembly_service?.kitchen?.service || "");
  const [kitchenDescription, setKitchenDescription] = useState(() => initialData?.assembly_service?.kitchen?.description || "");
  const [helpCarry, setHelpCarry] = useState(() => initialData?.help_carry || "");
  const [pickupFloor, setPickupFloor] = useState(() => initialData?.pickup_floor || "");
  const [pickupElevator, setPickupElevator] = useState(() => !!initialData?.pickup_elevator);
  const [deliveryFloor, setDeliveryFloor] = useState(() => initialData?.delivery_floor || "");
  const [deliveryElevator, setDeliveryElevator] = useState(() => !!initialData?.delivery_elevator);
  const [images, setImages] = useState([]);
  const [notes, setNotes] = useState(() => initialData?.notes || "");
  const [error, setError] = useState("");

  const isWholePlace = moveSubType === "Gesamte Wohnung" || moveSubType === "Gesamtes Haus";
  const allAreaKeys = MOVE_AREAS.map((a) => a.key);
  const selectedAreaDefs = useMemo(() => MOVE_AREAS.filter((a) => areas.includes(a.key)), [areas]);

  const setItemField = (area, itemKey, field, value) => {
    const k = `${area}::${itemKey}`;
    setItems((prev) => ({ ...prev, [k]: { ...(prev[k] || { quantity: 0 }), area, item: itemKey, [field]: value } }));
  };

  const selectedItemsFlat = useMemo(() => Object.values(items).filter((i) => Number(i.quantity) > 0), [items]);

  const areaItemCount = (area) =>
    area.items.filter((it) => Number(items[`${area.key}::${it.key}`]?.quantity) > 0).length +
    (area.isCustomArea ? Object.values(items).filter((i) => i.area === area.key && Number(i.quantity) > 0).length : 0);

  const kitchenItemsSelected = selectedItemsFlat.some((i) => i.area === KITCHEN_ROOM_KEY);

  const assemblableSelected = useMemo(() => selectedItemsFlat.filter((i) => ASSEMBLY_ELIGIBLE_KEYS.includes(i.item)), [selectedItemsFlat]);

  // The assembly question is only asked when the customer actually picked
  // something that can be taken apart (Regal, Kleiderschrank, Bett, ...) or
  // any kitchen item. If they only picked a sofa, a TV or moving boxes there
  // is nothing to disassemble, so the question — and everything under it —
  // is skipped and `assembly_service.answer` is saved as "Nein".
  const assemblyApplicable = assemblableSelected.length > 0 || kitchenItemsSelected;

  const hasMotorcycle = selectedItemsFlat.some((i) => i.item === "Motorrad" && Number(i.quantity) > 0);

  const buildPayload = () => ({
    move_type: "Umzug",
    move_sub_type: moveSubType,
    areas,
    items: selectedItemsFlat,
    heavy_items_answer: heavyAnswer,
    heavy_items: heavyAnswer === "Ja" ? Object.values(heavyItems).filter((i) => Number(i.quantity) > 0) : [],
    assembly_service: {
      // When nothing assemblable was selected the question is never shown,
      // so we record an explicit "Nein" rather than leaving it empty.
      answer: assemblyApplicable ? assemblyAnswer : "Nein",
      notes: assemblyApplicable && assemblyAnswer === "Ich bin nicht sicher" ? assemblyNotes : null,
      consultation_requested: assemblyApplicable && assemblyAnswer === "Ich bin nicht sicher" ? assemblyConsult : null,
      items: assemblyApplicable && assemblyAnswer === "Ja" ? Object.values(assemblyItems).filter((i) => i.service) : [],
      kitchen:
        assemblyApplicable && assemblyAnswer === "Ja" && kitchenItemsSelected && assemblyItems[KITCHEN_ROOM_KEY]
          ? { service: kitchenService, description: kitchenDescription }
          : null,
    },
    help_carry: helpCarry || "0",
    pickup_floor: helpCarry && helpCarry !== "0" ? pickupFloor || null : null,
    pickup_elevator: helpCarry && helpCarry !== "0" ? pickupElevator : false,
    delivery_floor: helpCarry && helpCarry !== "0" ? deliveryFloor || null : null,
    delivery_elevator: helpCarry && helpCarry !== "0" ? deliveryElevator : false,
    special_transport_required: hasMotorcycle,
    notes: notes || null,
  });

  const validate = () => {
    if (!moveSubType) return t("specialItems.moving.errors.select_scope");
    if (areas.length === 0) return t("specialItems.moving.errors.select_rooms");
    if (selectedItemsFlat.length === 0) return t("specialItems.moving.errors.select_items");
    if (selectedItemsFlat.find((i) => i.item === "Sonstiges" && !i.description))
      return t("specialItems.moving.errors.describe_custom_item");
    if (!heavyAnswer) return t("specialItems.moving.errors.answer_heavy");
    if (assemblyApplicable) {
      if (!assemblyAnswer) return t("specialItems.moving.errors.answer_assembly");
      if (assemblyAnswer === "Ja") {
        const invalid = Object.values(assemblyItems).some((i) => {
          if (i.item === KITCHEN_ROOM_KEY) return false;
          const original = selectedItemsFlat.find((s) => s.item === i.item);
          return i.service && original && Number(i.quantity) > Number(original.quantity);
        });
        if (invalid) return t("specialItems.moving.errors.assembly_quantity_exceeds");
      }
    }
    if (!helpCarry) return t("specialItems.moving.errors.answer_help_carry");
    return null;
  };

  const canSubmit =
    !!moveSubType &&
    areas.length > 0 &&
    selectedItemsFlat.length > 0 &&
    !!heavyAnswer &&
    (!assemblyApplicable || !!assemblyAnswer) &&
    !!helpCarry;

  const handleSubmit = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    onComplete(buildPayload(), images);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Card className="p-3 mb-4">
      {error && (
        <Alert variant="danger" className="d-flex align-items-center gap-2">
          <FaExclamationTriangle /> {error}
        </Alert>
      )}

      <Section show first>
        <StepHeader title={t("specialItems.moving.title")} />
        <SingleSelect name="move-sub-type" options={MOVE_SUB_TYPES} value={moveSubType} onChange={setMoveSubType} />
      </Section>

      <Section show={!!moveSubType}>
        <SectionDivider />
        <StepHeader
          title={isWholePlace ? t("specialItems.moving.select_rooms") : t("specialItems.furniture.title")}
          subtitle={t("specialItems.furniture.subtitle")}
        />
        {isWholePlace && (
          <Button
            variant={areas.length === allAreaKeys.length ? "primary" : "outline-primary"}
            size="sm"
            className="rounded-pill mb-2"
            onClick={() => setAreas(areas.length === allAreaKeys.length ? [] : allAreaKeys)}
          >
            {areas.length === allAreaKeys.length 
              ? t("specialItems.moving.deselect_all_rooms") 
              : t("specialItems.moving.select_all_rooms")}
          </Button>
        )}
        <MultiSelect name="area" options={allAreaKeys} values={areas} onChange={setAreas} />
      </Section>

      <Section show={areas.length > 0}>
        <SectionDivider />
        <StepHeader title={t("specialItems.furniture.select_items")} />
        <Accordion defaultActiveKey={selectedAreaDefs[0]?.key} alwaysOpen>
          {selectedAreaDefs.map((area) => (
            <Accordion.Item eventKey={area.key} key={area.key}>
              <Accordion.Header>
                <span className="d-flex align-items-center gap-2">
                  {area.key}
                  {areaItemCount(area) > 0 && <Badge bg="primary">{areaItemCount(area)}</Badge>}
                </span>
              </Accordion.Header>
              <Accordion.Body>
                {area.isCustomArea ? (
                  <CustomAreaItems areaKey={area.key} items={items} setItemField={setItemField} />
                ) : (
                  area.items.map((it) => {
                    const k = `${area.key}::${it.key}`;
                    const current = items[k] || { quantity: 0 };
                    return (
                      <div key={k} className="mb-2 pb-2 border-bottom">
                        <Row className="align-items-center">
                          <Col xs={7} sm={5}>
                            {it.key}
                          </Col>
                          <Col xs={5} sm={3}>
                            <Counter value={current.quantity} onChange={(v) => setItemField(area.key, it.key, "quantity", v)} />
                          </Col>
                        </Row>
                        {Number(current.quantity) > 0 && it.specialTransport && (
                          <Alert variant="info" className="mt-2 py-2 small">
                            {t("specialItems.moving.special_transport")}
                          </Alert>
                        )}
                        {Number(current.quantity) > 0 && it.typeOptions && (
                          <div className="mt-2">
                            <div className="small text-muted mb-1">{t("specialItems.furniture.type")}</div>
                            <SingleSelect
                              name={`${k}-type`}
                              options={it.typeOptions}
                              value={current.type}
                              onChange={(v) => setItemField(area.key, it.key, "type", v)}
                            />
                          </div>
                        )}
                        {Number(current.quantity) > 0 && it.sizeOptions && (
                          <div className="mt-2">
                            <div className="small text-muted mb-1">{it.sizeLabel === "weight" ? t("specialItems.furniture.weight") : t("specialItems.furniture.size")}</div>
                            <SingleSelect
                              name={`${k}-size`}
                              options={it.sizeOptions}
                              value={current.size}
                              onChange={(v) => setItemField(area.key, it.key, "size", v)}
                            />
                          </div>
                        )}
                        {Number(current.quantity) > 0 && it.requiresDescription && (
                          <Form.Group className="mt-2">
                            <Form.Label className="small text-muted">{t("specialItems.furniture.description")}</Form.Label>
                            <Form.Control
                              value={current.description || ""}
                              onChange={(e) => setItemField(area.key, it.key, "description", e.target.value)}
                            />
                          </Form.Group>
                        )}
                      </div>
                    );
                  })
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
        {selectedItemsFlat.length > 0 && <div className="text-muted small mt-2">{selectedItemsFlat.length} {t("specialItems.moving.items_selected")}</div>}
      </Section>

      <Section show={selectedItemsFlat.length > 0}>
        <SectionDivider />
        <StepHeader 
          title={t("specialItems.furniture.heavy_items")} 
          subtitle={t("specialItems.furniture.heavy_items_question", "Möchten Sie welche hinzufügen?")} 
        />
        <SingleSelect name="heavy-answer" options={[t("Ja"), t("Nein")]} value={heavyAnswer} onChange={setHeavyAnswer} />
        {heavyAnswer === "Ja" && (
          <div className="mt-3">
            {MOVE_HEAVY_ITEMS.map((it) => {
              const current = heavyItems[it.key] || { quantity: 0 };
              return (
                <div key={it.key} className="mb-2 pb-2 border-bottom">
                  <Row className="align-items-center">
                    <Col xs={7} sm={5}>
                      {it.key}
                    </Col>
                    <Col xs={5} sm={3}>
                      <Counter
                        value={current.quantity}
                        onChange={(v) => setHeavyItems((prev) => ({ ...prev, [it.key]: { ...current, item: it.key, quantity: v } }))}
                      />
                    </Col>
                  </Row>
                  {Number(current.quantity) > 0 && it.typeOptions && (
                    <SingleSelect
                      name={`${it.key}-type`}
                      options={it.typeOptions}
                      value={current.type}
                      onChange={(v) => setHeavyItems((prev) => ({ ...prev, [it.key]: { ...current, type: v } }))}
                    />
                  )}
                  {Number(current.quantity) > 0 && it.sizeOptions && (
                    <SingleSelect
                      name={`${it.key}-size`}
                      options={it.sizeOptions}
                      value={current.size}
                      onChange={(v) => setHeavyItems((prev) => ({ ...prev, [it.key]: { ...current, size: v } }))}
                    />
                  )}
                  {Number(current.quantity) > 0 && it.requiresDescription && (
                    <Form.Control
                      className="mt-2"
                      placeholder={t("specialItems.furniture.description")}
                      value={current.description || ""}
                      onChange={(e) => setHeavyItems((prev) => ({ ...prev, [it.key]: { ...current, description: e.target.value } }))}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Skipped entirely when nothing assemblable was selected (e.g. only a
          sofa, a TV or moving boxes) — see `assemblyApplicable`. */}
      <Section show={!!heavyAnswer && assemblyApplicable}>
        <SectionDivider />
        <StepHeader title={t("specialItems.furniture.assembly_service")} />
        <SingleSelect name="assembly-answer" options={ASSEMBLY_SERVICE_OPTIONS} value={assemblyAnswer} onChange={setAssemblyAnswer} />

        {assemblyAnswer === "Ich bin nicht sicher" && (
          <Form.Group className="mt-2">
            <Form.Label className="small text-muted">{t("specialItems.furniture.please_describe")}</Form.Label>
            <Form.Control as="textarea" rows={2} value={assemblyNotes} onChange={(e) => setAssemblyNotes(e.target.value)} />
            <Form.Check
              className="mt-2"
              type="checkbox"
              id="assembly-consult"
              label={t("specialItems.furniture.consultation_request")}
              checked={assemblyConsult}
              onChange={(e) => setAssemblyConsult(e.target.checked)}
            />
          </Form.Group>
        )}

        {assemblyAnswer === "Ja" && (
          <div className="mt-3">
            {kitchenItemsSelected && (
              <div className="mb-3 pb-2 border-bottom">
                <Form.Check
                  type="checkbox"
                  id="assembly-kueche"
                  label={t("specialItems.moving.kitchen")}
                  checked={!!assemblyItems[KITCHEN_ROOM_KEY]}
                  onChange={(e) =>
                    setAssemblyItems((prev) => {
                      const next = { ...prev };
                      if (e.target.checked) next[KITCHEN_ROOM_KEY] = { item: KITCHEN_ROOM_KEY };
                      else delete next[KITCHEN_ROOM_KEY];
                      return next;
                    })
                  }
                />
                {assemblyItems[KITCHEN_ROOM_KEY] && (
                  <div className="ms-4 mt-2">
                    <div className="small text-muted mb-1">{t("specialItems.furniture.kitchen_service")}</div>
                    <SingleSelect name="kitchen-service" options={KITCHEN_SERVICE_OPTIONS} value={kitchenService} onChange={setKitchenService} />
                    <Form.Control
                      className="mt-2"
                      placeholder={t("specialItems.furniture.kitchen_description")}
                      value={kitchenDescription}
                      onChange={(e) => setKitchenDescription(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}

            {assemblableSelected.map((sel) => {
              const current = assemblyItems[sel.item] || { quantity: sel.quantity };
              return (
                <div key={sel.item} className="mb-3 pb-2 border-bottom">
                  <Form.Check
                    type="checkbox"
                    id={`assembly-${sel.item}`}
                    label={`${sel.item}${sel.type ? ` – ${sel.type}` : ""}${sel.size ? ` – ${sel.size}` : ""}`}
                    checked={!!assemblyItems[sel.item]}
                    onChange={(e) =>
                      setAssemblyItems((prev) => {
                        const next = { ...prev };
                        if (e.target.checked) next[sel.item] = { item: sel.item, quantity: sel.quantity, service: "" };
                        else delete next[sel.item];
                        return next;
                      })
                    }
                  />
                  {assemblyItems[sel.item] && (
                    <div className="ms-4 mt-2">
                      <div className="small text-muted mb-1">{t("specialItems.furniture.select_service")}</div>
                      <SingleSelect
                        name={`${sel.item}-service`}
                        options={ASSEMBLY_LEVEL_OPTIONS}
                        value={current.service}
                        onChange={(v) => setAssemblyItems((prev) => ({ ...prev, [sel.item]: { ...prev[sel.item], service: v } }))}
                      />
                      <div className="small text-muted mb-1">{t("specialItems.furniture.assembly_quantity", { max: sel.quantity })}</div>
                      <Counter
                        value={current.quantity}
                        max={sel.quantity}
                        onChange={(v) => setAssemblyItems((prev) => ({ ...prev, [sel.item]: { ...prev[sel.item], quantity: v } }))}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section show={!!heavyAnswer && (!assemblyApplicable || !!assemblyAnswer)}>
        <SectionDivider />
        <HelpAndFloorsStep
          helpCarry={helpCarry}
          setHelpCarry={setHelpCarry}
          pickupFloor={pickupFloor}
          setPickupFloor={setPickupFloor}
          pickupElevator={pickupElevator}
          setPickupElevator={setPickupElevator}
          deliveryFloor={deliveryFloor}
          setDeliveryFloor={setDeliveryFloor}
          deliveryElevator={deliveryElevator}
          setDeliveryElevator={setDeliveryElevator}
        />
      </Section>

      <Section show={!!helpCarry}>
        <SectionDivider />
        <StepHeader title={t("specialItems.furniture.photos")} subtitle={t("specialItems.furniture.optional", "Optional")} />
        <Card className="p-3 p-md-4 mb-4 border-0 shadow-sm rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("specialItems.furniture.photos")}</h5>
          <Form.Group className="mb-3">
            <Form.Label>{t("specialItems.furniture.add_photos")}</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setImages(Array.from(e.target.files || []))}
            />
            <Form.Text className="text-muted">
              {t("specialItems.furniture.photos_hint")}
            </Form.Text>
          </Form.Group>

          {images.length > 0 && (
            <div className="mt-3">
              <h6>{t("specialItems.furniture.selected_photos")}</h6>
              <Row>
                {images.map((image, index) => (
                  <Col
                    xs={6}
                    sm={4}
                    md={3}
                    key={index}
                    className="mb-2 position-relative"
                  >
                    {image instanceof File ? (
                      <img
                        src={URL.createObjectURL(image)}
                        alt={t("specialItems.furniture.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                      />
                    ) : (
                      <img
                        src={image}
                        alt={t("specialItems.furniture.upload_alt", { index: index + 1 })}
                        style={{
                          width: "100%",
                          height: "80px",
                          objectFit: "cover",
                        }}
                        className="rounded"
                        onError={(e) => {
                          console.error("Error loading image:", image);
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0"
                      onClick={() => removeImage(index)}
                      style={{ transform: "translate(50%, -50%)" }}
                    >
                      ×
                    </Button>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Card>

        <Card className="p-3 p-md-4 mb-4 border-0 shadow-sm rounded-3 bg-light">
          <h5 className="fw-bold mb-3">{t("specialItems.furniture.additional_notes_title")}</h5>
          <Form.Group>
            <Form.Label>{t("specialItems.furniture.description")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder={t("specialItems.furniture.notes_placeholder")}
              value={notes} 
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>
        </Card>

        <SectionDivider />
        <MoveSummary
          areas={areas}
          items={selectedItemsFlat}
          heavyItems={heavyAnswer === "Ja" ? Object.values(heavyItems).filter((i) => i.quantity > 0) : []}
          assemblyAnswer={assemblyAnswer}
          assemblyItems={Object.values(assemblyItems).filter((i) => i.service || i.item === KITCHEN_ROOM_KEY)}
        />
      </Section>

      <StickyActionBar
        onBack={onEditRoute}
        onSubmit={handleSubmit}
        disabled={!canSubmit}
        disabledHint={!canSubmit ? t("specialItems.moving.complete_questions", "Bitte beantworten Sie zuerst alle Fragen oben.") : null}
      />
    </Card>
  );
};

export default MoveWizard;